import * as THREE from 'three';
import { HorizontalBlurShader } from 'three/examples/jsm/shaders/HorizontalBlurShader.js';
import { VerticalBlurShader } from 'three/examples/jsm/shaders/VerticalBlurShader.js';
import { FLOOR_DEPTH_OFFSET } from './depthOffset';
import { swapMaterials } from './materialSwap';

const RESOLUTION = 512;
const FOOTPRINT_MARGIN = 1.6;
const BLUR_TEXELS = 7;
const MIN_HEIGHT_RATIO = 0.02;

const DEPTH_VERTEX = /* glsl */ `
  #include <clipping_planes_pars_vertex>
  uniform float uFloor;
  uniform float uHeight;
  varying float vRise;
  void main() {
    vec4 world = modelMatrix * vec4(position, 1.0);
    vRise = (world.z - uFloor) / uHeight;
    vec4 mvPosition = viewMatrix * world;
    gl_Position = projectionMatrix * mvPosition;
    #include <clipping_planes_vertex>
  }
`;

const DEPTH_FRAGMENT = /* glsl */ `
  #include <clipping_planes_pars_fragment>
  varying float vRise;
  void main() {
    #include <clipping_planes_fragment>
    float contact = 1.0 - clamp(vRise, 0.0, 1.0);
    gl_FragColor = vec4(0.0, 0.0, 0.0, contact * contact);
  }
`;

function blurMaterial(shader: typeof HorizontalBlurShader | typeof VerticalBlurShader) {
  return new THREE.ShaderMaterial({
    uniforms: THREE.UniformsUtils.clone(shader.uniforms),
    vertexShader: shader.vertexShader,
    fragmentShader: shader.fragmentShader,
    depthTest: false,
  });
}

/** Drawn once from below the floor, so it costs nothing per frame. */
export class ContactShadow {
  readonly mesh: THREE.Mesh<THREE.PlaneGeometry, THREE.MeshBasicMaterial>;
  private readonly target = new THREE.WebGLRenderTarget(RESOLUTION, RESOLUTION);
  private readonly scratch = new THREE.WebGLRenderTarget(RESOLUTION, RESOLUTION);
  private readonly castCamera = new THREE.OrthographicCamera();
  private readonly heightMaterial = new THREE.ShaderMaterial({
    uniforms: { uFloor: { value: 0 }, uHeight: { value: 1 } },
    vertexShader: DEPTH_VERTEX,
    fragmentShader: DEPTH_FRAGMENT,
    side: THREE.DoubleSide,
    clipping: true,
  });
  private readonly quad = new THREE.Mesh(new THREE.PlaneGeometry(2, 2));
  private readonly quadCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly horizontal = blurMaterial(HorizontalBlurShader);
  private readonly vertical = blurMaterial(VerticalBlurShader);
  private floor = 0;
  private shown = true;

  constructor(isDark: boolean) {
    this.target.texture.generateMipmaps = false;
    this.scratch.texture.generateMipmaps = false;
    this.quadCamera.position.z = 0.5;
    this.quadCamera.updateMatrixWorld();
    this.horizontal.uniforms.tDiffuse.value = this.target.texture;
    this.vertical.uniforms.tDiffuse.value = this.scratch.texture;
    const material = new THREE.MeshBasicMaterial({
      map: this.target.texture,
      color: 0x000000,
      transparent: true,
      opacity: isDark ? 1 : 0.78,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: FLOOR_DEPTH_OFFSET,
      polygonOffsetUnits: FLOOR_DEPTH_OFFSET,
      toneMapped: false,
    });
    this.mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), material);
    this.mesh.renderOrder = -2;
    this.mesh.visible = false;
  }

  render(
    renderer: THREE.WebGLRenderer,
    model: THREE.Object3D,
    box: THREE.Box3,
    floor: number,
    clip: THREE.Plane | null = null,
  ): void {
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    const side = Math.max(size.x, size.y) * FOOTPRINT_MARGIN;
    if (!Number.isFinite(side) || side <= 0) return;
    const height = Math.max(box.max.z - floor, side * MIN_HEIGHT_RATIO);

    const camera = this.castCamera;
    camera.left = -side / 2;
    camera.right = side / 2;
    camera.top = side / 2;
    camera.bottom = -side / 2;
    camera.near = 0;
    camera.far = height;
    camera.updateProjectionMatrix();
    camera.position.set(center.x, center.y, floor);
    camera.rotation.set(Math.PI, 0, 0);
    camera.updateMatrixWorld();

    const heightMaterial = this.heightMaterial;
    heightMaterial.uniforms.uFloor.value = floor;
    heightMaterial.uniforms.uHeight.value = height;
    heightMaterial.clippingPlanes = clip ? [clip] : null;

    const previousTarget = renderer.getRenderTarget();
    const previousClear = renderer.getClearColor(new THREE.Color());
    const previousAlpha = renderer.getClearAlpha();
    renderer.setClearColor(0x000000, 0);
    renderer.setRenderTarget(this.target);
    renderer.clear();
    const restore = swapMaterials(model, heightMaterial);
    try {
      renderer.render(model, camera);
    } finally {
      restore();
    }
    this.blur(renderer, BLUR_TEXELS);
    this.blur(renderer, BLUR_TEXELS * 0.5);
    renderer.setRenderTarget(previousTarget);
    renderer.setClearColor(previousClear, previousAlpha);

    this.floor = floor;
    this.mesh.position.set(center.x, center.y, floor);
    // The map was drawn looking up, which mirrors Y.
    this.mesh.scale.set(side, -side, 1);
    this.mesh.visible = true;
  }

  /** Only a solid casts one; through a see-through part its whole footprint shows. */
  show(shown: boolean): void {
    this.shown = shown;
  }

  /** From underneath, the shadow would sit in front of the part it belongs to. */
  update(camera: THREE.Camera): void {
    this.mesh.material.visible = this.shown && camera.position.z > this.floor;
  }

  dispose(): void {
    this.target.dispose();
    this.scratch.dispose();
    this.heightMaterial.dispose();
    this.quad.geometry.dispose();
    this.horizontal.dispose();
    this.vertical.dispose();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }

  private blur(renderer: THREE.WebGLRenderer, texels: number): void {
    const { quad, quadCamera, horizontal, vertical } = this;
    horizontal.uniforms.h.value = texels / RESOLUTION;
    vertical.uniforms.v.value = texels / RESOLUTION;

    quad.material = horizontal;
    renderer.setRenderTarget(this.scratch);
    renderer.clear();
    renderer.render(quad, quadCamera);
    quad.material = vertical;
    renderer.setRenderTarget(this.target);
    renderer.clear();
    renderer.render(quad, quadCamera);
  }
}
